import { Box, Grid, Hidden, Link, Paper, Theme, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { ProviderInfo } from '../../../types/type';
import { getPrettyPhoneNumber } from '../../../utils/Utilities';
import VisitWebSite from '../VisitWebsite/VisitWebsite';

interface ContactInfo {
  providerInfo: ProviderInfo;
  isAuto: boolean;
}

interface HeaderRowProps {
  isAuto: boolean;
  provider: ProviderInfo;
}

interface ProviderRowProps {
  isAuto: boolean;
  provider: ProviderInfo;
}

const ContactCard: React.FC<ContactInfo> = ({ providerInfo, isAuto }) => {
  const theme: Theme = useTheme();
  const providerInfoRowStyle = {
    ...theme.custom.styles.primaryDescription,
  };

  const HeaderRow: React.FC<HeaderRowProps> = ({ isAuto, provider }) => (
    <React.Fragment>
      {/* Desktop browser view */}
      <Hidden smDown>
        <Grid container sx={{ px: '.5rem', paddingTop: '1.5rem' }}>
          <Grid item xs={4}>
            <Typography sx={theme.custom.styles.contactCardHeaderRowStyle}>
              {isAuto ? 'Shop Name' : 'Contact'}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography sx={theme.custom.styles.contactCardHeaderRowStyle}>{isAuto ? 'Address' : 'Website'}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography sx={theme.custom.styles.contactCardHeaderRowStyle}>{'Contact Methods'}</Typography>
          </Grid>
        </Grid>
      </Hidden>
      {/* Mobile browser view */}
      <Hidden smUp>
        <Paper
          variant="outlined"
          sx={{
            width: '100%',
            paddingTop: '0.5rem',
            backgroundColor: theme.custom.styles.oddRow.backgroundColor,
            borderRadius: isAuto ? theme.custom.styles.borderRadius.radiusDefault : '0px',
            border: isAuto
              ? theme.custom.styles.showBorderAroundListItems
                ? `${theme.custom.styles.listItemBorderDefaultStyle} ${theme.custom.styles.radioandcheckboxStyle.defaultBorder}`
                : theme.custom.colors.bodyColor
              : '0px',
          }}
        >
          <Grid container item xs={12} spacing={1}>
            <Grid item xs={4}>
              <Typography
                sx={{
                  ...theme.custom.styles.contactCardHeaderRowStyle,
                  marginLeft: '.5rem',
                }}
              >
                {isAuto ? 'Shop Name' : 'Contact'}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography sx={providerInfoRowStyle}>{provider.providerName}</Typography>
            </Grid>
          </Grid>
          <Grid container item spacing={1} sx={{ marginTop: '1rem' }}>
            <Grid item xs={4}>
              <Typography
                sx={{
                  ...theme.custom.styles.contactCardHeaderRowStyle,
                  marginLeft: '.5rem',
                }}
              >
                {isAuto ? 'Address' : 'Website'}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              {!isAuto && provider.providerUrl && <VisitWebSite websiteAddress={provider.providerUrl} />}
              {isAuto && provider.providerAddress && (
                <Typography sx={providerInfoRowStyle}>{provider.providerAddress}</Typography>
              )}
            </Grid>
          </Grid>
          <Grid container item spacing={1} sx={{ marginTop: '1rem', marginBottom: '.5rem' }}>
            <Grid item xs={4}>
              <Typography
                sx={{
                  ...theme.custom.styles.contactCardHeaderRowStyle,
                  marginLeft: '.5rem',
                }}
              >
                {'Contact Methods'}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              {provider.providerPhone && (
                <Typography sx={providerInfoRowStyle}>
                  {'Telephone:'}
                  <Link
                    underline="none"
                    sx={theme.custom.styles.phoneNumberAndWebSiteStyle}
                    href={`tel:${getPrettyPhoneNumber(provider.providerPhone)}`}
                  >
                    {' ' + getPrettyPhoneNumber(provider.providerPhone)}
                  </Link>
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Hidden>
    </React.Fragment>
  );

  const ProviderRow: React.FC<ProviderRowProps> = ({ isAuto, provider }) => (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        backgroundColor: theme.custom.styles.evenRow.backgroundColor,
        borderRadius: isAuto ? theme.custom.styles.borderRadius.radiusDefault : '0px',
        border: isAuto
          ? theme.custom.styles.showBorderAroundListItems
            ? `${theme.custom.styles.listItemBorderDefaultStyle} ${theme.custom.styles.radioandcheckboxStyle.defaultBorder}`
            : theme.custom.colors.bodyColor
          : '0px',
      }}
    >
      <Grid container xs={12} sx={{ p: '.5rem' }}>
        <Grid
          item
          xs={4}
          sx={theme.custom.styles.singleServiceSummaryHr ? {} : theme.custom.styles.contactCardRowStyling}
        >
          <Typography sx={providerInfoRowStyle}>{provider.providerName}</Typography>
        </Grid>
        <Grid
          item
          xs={4}
          sx={theme.custom.styles.singleServiceSummaryHr ? {} : theme.custom.styles.contactCardRowStyling}
        >
          {!isAuto && provider.providerUrl && <VisitWebSite websiteAddress={provider.providerUrl} />}
          {isAuto && provider.providerAddress && (
            <Typography sx={providerInfoRowStyle}>{provider.providerAddress}</Typography>
          )}
        </Grid>
        <Grid
          item
          xs={4}
          sx={theme.custom.styles.singleServiceSummaryHr ? {} : theme.custom.styles.contactCardRowStyling}
        >
          {provider.providerPhone && (
            <Typography sx={providerInfoRowStyle}>
              {'Telephone:'}
              <Link
                underline="none"
                sx={theme.custom.styles.phoneNumberAndWebSiteStyle}
                href={`tel:${getPrettyPhoneNumber(provider.providerPhone)}`}
              >
                {' ' + getPrettyPhoneNumber(provider.providerPhone)}
              </Link>
            </Typography>
          )}
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <div>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container>
          <Grid container item>
            <HeaderRow isAuto={isAuto} provider={providerInfo} />
            {theme.custom.styles.singleServiceSummaryHr && (
              <hr
                style={{
                  ...theme.custom.styles.listItemDividerHrStyle,
                  width: '100%',
                }}
              />
            )}
          </Grid>
          <Hidden smDown>
            <ProviderRow isAuto={isAuto} provider={providerInfo} />
          </Hidden>
          {theme.custom.styles.singleServiceSummaryDashedHr && (
            <hr
              style={{
                border: theme.custom.styles.serviceProviderSummaryHr,
                width: '100%',
              }}
            />
          )}
        </Grid>
      </Box>
    </div>
  );
};

export default ContactCard;
